import type {
  ChangeFundCommandData,
  FundRiskLevel,
  PrognosisAssetFraction,
} from '@festina/unitlink-general-types/unitlink'
import { typeUtils } from '@festina-life/core'
import {
  FormikFieldArray,
  FormikForm,
  FormikPercentageInput,
  FormikSelect,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'

import { useFundContext } from '../../../context/fundContext'
import { useEnumOptions } from '../../../hooks/useEnumOptions'
import type { CommandPaletteFormProps } from '../../commandActions/commandPalette/CommandPaletteModalButton'
import { PrognosisAssetFractionFields } from './PrognosisAssetFractionFields'

const validateKey = typeUtils.createValidator<ChangeFundCommandData>()

export const ChangeFundForm = (
  props: CommandPaletteFormProps<ChangeFundCommandData>,
) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()

  const { currentFundVersion } = useFundContext()

  const initialValues: ChangeFundCommandData = {
    _type: 'ChangeFundCommandData',
    isinKey: currentFundVersion?.isinKey,
    newAssetManagerId: currentFundVersion?.assetManagerId,
    newFundRiskLevel: currentFundVersion?.fundRiskLevel,
    newInternalCost: currentFundVersion?.internalCost,
    newInternalId: currentFundVersion?.internalId,
    newName: currentFundVersion?.name,
    newShortName: currentFundVersion?.shortName,
    newPrognosisAssetFractions: currentFundVersion?.prognosisAssetFractions,
  }

  const fundRiskLevelOptions = useEnumOptions('FundRiskLevel', 'unitlink')

  return (
    <FormikForm initialValues={initialValues} {...props}>
      <FormikTextInput
        name={validateKey('isinKey')}
        label={translate('funds.fundItem.isinKey')}
      />
      <FormikTextInput
        name={validateKey('newInternalId')}
        label={translate('funds.fundItem.internalId')}
      />
      <FormikTextInput
        name={validateKey('newAssetManagerId')}
        label={translate('funds.fundItem.assetManagerId')}
      />
      <FormikTextInput
        name={validateKey('newName')}
        label={translate('funds.fundItem.name')}
      />
      <FormikTextInput
        name={validateKey('newShortName')}
        label={translate('funds.fundItem.shortName')}
      />
      <FormikSelect
        name={validateKey('newFundRiskLevel')}
        label={translate('funds.fundItem.fundRiskLevel')}
        options={fundRiskLevelOptions}
        renderFn={(x: FundRiskLevel) =>
          translateEnum('unitLink.enum.FundRiskLevel', x)
        }
      />
      <FormikPercentageInput
        name={validateKey('newInternalCost')}
        label={translate('funds.fundItem.internalCost')}
      />
      <FormikFieldArray
        name={validateKey('newPrognosisAssetFractions')}
        label={translate('funds.fundItem.prognosisAssetFractions')}
        elementLabel={translate('funds.prognosisAssetFraction.element')}
        elementInitializer={() =>
          ({ _type: 'PrognosisAssetFraction' }) satisfies PrognosisAssetFraction
        }
        ChildElement={PrognosisAssetFractionFields}
      />
    </FormikForm>
  )
}
